import { create } from "zustand";

export type ModalType = "alert" | "confirm" | "destructive";

export interface ModalOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}

interface ModalState {
  isOpen: boolean;
  type: ModalType;
  options: ModalOptions;
  resolve: ((value: boolean) => void) | null;
}

interface ModalStore {
  modal: ModalState;
  openModal: (type: ModalType, options: ModalOptions) => Promise<boolean>;
  closeModal: (result: boolean) => void;
}

const defaultState: ModalState = {
  isOpen: false,
  type: "alert",
  options: { message: "" },
  resolve: null,
};

export const useModalStore = create<ModalStore>((set, get) => ({
  modal: defaultState,

  openModal: (type, options) => {
    return new Promise<boolean>((resolve) => {
      set({
        modal: {
          isOpen: true,
          type,
          options,
          resolve,
        },
      });
    });
  },

  closeModal: (result) => {
    const { modal } = get();
    if (modal.resolve) {
      modal.resolve(result);
    }
    set({ modal: defaultState });
  },
}));

const modalService = {
  confirm: (message: string, options?: Omit<ModalOptions, "message">) => {
    return useModalStore
      .getState()
      .openModal("confirm", { message, ...options });
  },

  alert: (message: string, options?: Omit<ModalOptions, "message">) => {
    return useModalStore.getState().openModal("alert", { message, ...options });
  },

  destructive: (message: string, options?: Omit<ModalOptions, "message">) => {
    return useModalStore.getState().openModal("destructive", {
      message,
      confirmText: options?.confirmText ?? "삭제",
      ...options,
    });
  },
};

export { modalService };
